import AsyncStorage from '@react-native-async-storage/async-storage';
import websiteAPI from './WebsiteAPI';

class AnalyticsService {
  constructor() {
    this.cache = new Map();
    this.cacheTimeout = 5 * 60 * 1000; // 5 minutes
    this.realTimeData = new Map();
    this.realTimeInterval = null;
    this.listeners = new Map();
  }

  // Event Listener Management
  addEventListener(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event).add(callback);
  }

  removeEventListener(event, callback) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).delete(callback);
    }
  }

  emit(event, data) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Error in analytics event listener for ${event}:`, error);
        }
      });
    }
  }

  // Analytics Data Fetching
  async getAnalytics(websiteId, timeRange = '7d', forceRefresh = false) {
    const cacheKey = `analytics_${websiteId}_${timeRange}`;
    
    // Check cache first
    if (!forceRefresh && this.cache.has(cacheKey)) {
      const cached = this.cache.get(cacheKey);
      if (Date.now() - cached.timestamp < this.cacheTimeout) {
        return cached.data;
      }
    }

    try {
      const data = await websiteAPI.getAnalytics(websiteId, { range: timeRange });
      
      // Cache the data
      this.cache.set(cacheKey, {
        data,
        timestamp: Date.now(),
      });

      this.emit('analytics_updated', { websiteId, timeRange, data });
      return data;
    } catch (error) {
      console.error('Error fetching analytics:', error);
      throw error;
    }
  }

  // Real-time Analytics
  startRealTimeMonitoring(websiteId, interval = 30000) {
    this.stopRealTimeMonitoring();
    
    this.realTimeInterval = setInterval(async () => {
      try {
        const data = await this.getRealTimeData(websiteId);
        this.realTimeData.set(websiteId, data);
        this.emit('real_time_update', { websiteId, data });
      } catch (error) {
        console.error('Error fetching real-time data:', error);
      }
    }, interval);
  }

  stopRealTimeMonitoring() {
    if (this.realTimeInterval) {
      clearInterval(this.realTimeInterval);
      this.realTimeInterval = null;
    }
  }

  async getRealTimeData(websiteId) {
    // This would typically connect to a real-time analytics service
    // For now, we'll simulate real-time data
    return {
      activeUsers: Math.floor(Math.random() * 100) + 20,
      currentPageViews: Math.floor(Math.random() * 50) + 10,
      topActivePages: [
        { path: '/', users: Math.floor(Math.random() * 20) + 5 },
        { path: '/services', users: Math.floor(Math.random() * 15) + 3 },
        { path: '/about', users: Math.floor(Math.random() * 10) + 2 },
      ],
      timestamp: Date.now(),
    };
  }

  // Analytics Calculations
  calculateGrowthRate(current, previous) {
    if (previous === 0) return current > 0 ? 100 : 0;
    return ((current - previous) / previous) * 100;
  }

  calculateConversionRate(conversions, visitors) {
    if (visitors === 0) return 0;
    return (conversions / visitors) * 100;
  }

  calculateBounceRate(bounces, sessions) {
    if (sessions === 0) return 0;
    return (bounces / sessions) * 100;
  }

  calculateAverageSessionDuration(totalDuration, sessions) {
    if (sessions === 0) return 0;
    return totalDuration / sessions;
  }

  // Data Processing
  processTrafficData(rawData, timeRange) {
    const processed = {
      labels: [],
      datasets: [{
        data: [],
        color: (opacity = 1) => `rgba(102, 126, 234, ${opacity})`,
        strokeWidth: 2,
      }],
    };

    if (!rawData || !rawData.length) {
      return processed;
    }

    rawData.forEach(item => {
      processed.labels.push(this.formatDateLabel(item.date, timeRange));
      processed.datasets[0].data.push(item.visitors || 0);
    });

    return processed;
  }

  processSourcesData(rawData) {
    if (!rawData) return { labels: [], datasets: [{ data: [] }] };

    const sources = Object.entries(rawData);
    const colors = [
      (opacity = 1) => `rgba(102, 126, 234, ${opacity})`,
      (opacity = 1) => `rgba(16, 185, 129, ${opacity})`,
      (opacity = 1) => `rgba(245, 158, 11, ${opacity})`,
      (opacity = 1) => `rgba(239, 68, 68, ${opacity})`,
      (opacity = 1) => `rgba(139, 92, 246, ${opacity})`,
    ];

    return {
      labels: sources.map(([name]) => name.charAt(0).toUpperCase() + name.slice(1)),
      datasets: [{
        data: sources.map(([, value]) => value),
        colors: colors.slice(0, sources.length),
      }],
    };
  }

  processDeviceData(rawData) {
    if (!rawData) return { labels: [], datasets: [{ data: [] }] };

    const devices = Object.entries(rawData);
    
    return {
      labels: devices.map(([name]) => name.charAt(0).toUpperCase() + name.slice(1)),
      datasets: [{
        data: devices.map(([, value]) => value),
      }],
    };
  }

  // Performance Metrics
  async getPerformanceMetrics(websiteId, timeRange = '7d') {
    try {
      const data = await websiteAPI.getPerformanceMetrics(websiteId, timeRange);
      return this.processPerformanceData(data);
    } catch (error) {
      console.error('Error fetching performance metrics:', error);
      throw error;
    }
  }

  processPerformanceData(rawData) {
    if (!rawData) return {};

    return {
      pageLoadTime: this.formatDuration(rawData.pageLoadTime),
      firstContentfulPaint: this.formatDuration(rawData.firstContentfulPaint),
      largestContentfulPaint: this.formatDuration(rawData.largestContentfulPaint),
      cumulativeLayoutShift: rawData.cumulativeLayoutShift?.toFixed(3),
      timeToInteractive: this.formatDuration(rawData.timeToInteractive),
      performanceScore: rawData.performanceScore,
      seoScore: rawData.seoScore,
      accessibilityScore: rawData.accessibilityScore,
    };
  }

  // SEO Analytics
  async getSEOMetrics(websiteId) {
    try {
      const data = await websiteAPI.getSEOAnalysis(websiteId);
      return this.processSEOData(data);
    } catch (error) {
      console.error('Error fetching SEO metrics:', error);
      throw error;
    }
  }

  processSEOData(rawData) {
    if (!rawData) return {};

    return {
      overallScore: rawData.overallScore || 0,
      metaTags: rawData.metaTags || {},
      headings: rawData.headings || {},
      images: rawData.images || {},
      links: rawData.links || {},
      performance: rawData.performance || {},
      recommendations: rawData.recommendations || [],
    };
  }

  // Comparison Analytics
  async compareTimeRanges(websiteId, currentRange, previousRange) {
    try {
      const [currentData, previousData] = await Promise.all([
        this.getAnalytics(websiteId, currentRange),
        this.getAnalytics(websiteId, previousRange),
      ]);

      return this.calculateComparison(currentData, previousData);
    } catch (error) {
      console.error('Error comparing time ranges:', error);
      throw error;
    }
  }

  calculateComparison(current, previous) {
    const comparison = {};

    const metrics = ['totalVisitors', 'pageViews', 'bounceRate', 'avgSessionDuration', 'conversionRate', 'revenue'];
    
    metrics.forEach(metric => {
      const currentValue = current.overview?.[metric] || 0;
      const previousValue = previous.overview?.[metric] || 0;
      
      comparison[metric] = {
        current: currentValue,
        previous: previousValue,
        change: this.calculateGrowthRate(currentValue, previousValue),
        trend: currentValue > previousValue ? 'up' : currentValue < previousValue ? 'down' : 'stable',
      };
    });

    return comparison;
  }

  // Goal Tracking
  async trackGoal(websiteId, goalData) {
    try {
      const result = await websiteAPI.trackGoal(websiteId, goalData);
      this.emit('goal_tracked', { websiteId, goalData, result });
      return result;
    } catch (error) {
      console.error('Error tracking goal:', error);
      throw error;
    }
  }

  async getGoalMetrics(websiteId, timeRange = '30d') {
    try {
      const data = await websiteAPI.getGoalMetrics(websiteId, timeRange);
      return this.processGoalData(data);
    } catch (error) {
      console.error('Error fetching goal metrics:', error);
      throw error;
    }
  }

  processGoalData(rawData) {
    if (!rawData || !rawData.length) return [];

    return rawData.map(goal => ({
      ...goal,
      completionRate: this.calculateConversionRate(goal.completions, goal.attempts),
      trend: this.calculateGrowthRate(goal.currentPeriod, goal.previousPeriod),
    }));
  }

  // Custom Events
  async trackCustomEvent(websiteId, eventData) {
    try {
      const result = await websiteAPI.trackCustomEvent(websiteId, eventData);
      this.emit('custom_event_tracked', { websiteId, eventData, result });
      return result;
    } catch (error) {
      console.error('Error tracking custom event:', error);
      throw error;
    }
  }

  // Data Export
  async exportAnalytics(websiteId, timeRange, format = 'json') {
    try {
      const data = await this.getAnalytics(websiteId, timeRange);
      
      switch (format) {
        case 'csv':
          return this.convertToCSV(data);
        case 'pdf':
          return this.generatePDFReport(data);
        default:
          return data;
      }
    } catch (error) {
      console.error('Error exporting analytics:', error);
      throw error;
    }
  }

  convertToCSV(data) {
    // Convert analytics data to CSV format
    const headers = ['Date', 'Visitors', 'Page Views', 'Bounce Rate', 'Avg Session Duration'];
    const rows = data.traffic?.labels?.map((label, index) => [
      label,
      data.traffic.datasets[0].data[index],
      data.overview?.pageViews || 0,
      data.overview?.bounceRate || 0,
      data.overview?.avgSessionDuration || 0,
    ]) || [];

    return [headers, ...rows].map(row => row.join(',')).join('\n');
  }

  async generatePDFReport(data) {
    // This would generate a PDF report
    // Implementation would depend on the PDF library used
    return { message: 'PDF report generation not implemented' };
  }

  // Utility Methods
  formatDateLabel(date, timeRange) {
    const dateObj = new Date(date);
    
    switch (timeRange) {
      case '7d':
      case '30d':
        return dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      case '90d':
      case '1y':
        return dateObj.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
      default:
        return dateObj.toLocaleDateString();
    }
  }

  formatDuration(seconds) {
    if (seconds < 1) return `${(seconds * 1000).toFixed(0)}ms`;
    if (seconds < 60) return `${seconds.toFixed(2)}s`;
    
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds.toFixed(0)}s`;
  }

  formatNumber(num) {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    } else if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return num?.toString() || '0';
  }

  // Cache Management
  clearCache() {
    this.cache.clear();
  }

  getCacheSize() {
    return this.cache.size;
  }

  // Cleanup
  destroy() {
    this.stopRealTimeMonitoring();
    this.clearCache();
    this.listeners.clear();
    this.realTimeData.clear();
  }
}

// Create and export a singleton instance
const analyticsService = new AnalyticsService();
export default analyticsService;
